// Design system — single source of truth
import "./styles/global.css";
// V2 design tokens (--ah-*/--accent-* vars + glass/3D/orb utilities)
import "./styles/tokens.css";
// Legacy styles — kept for screens not yet migrated
import "./styles/style.css";
import "./styles/interview-rounds.css";
// Premium theme — loaded last to override everything
import "./styles/ample-premium.css";

import { Route, Routes } from "react-router-dom";
import UiReference from "./ui-reference-screen/index";
import SigninPage from "./screens/user-signup-signin/signin/index";
import SignupPage from "./screens/user-signup-signin/signup/index";
import Admin from "./screens/admin/index";
import NoRouteFound from "./screens/no-route-found/no-route-found";
import EntitySignupScreen from "./screens/entity-signup";
import User from "./screens/UserDashboard";
import AuthComponent from "./components/auth-component/auth-component";
import ResetPassword from "./screens/user-signup-signin/reset/index";
import ChangePassword from "./screens/change-password/index";
import Confirm from "./screens/user-signup-signin/confirm/index";
import Interview from "./screens/interview";
import DeviceChecking from "./screens/device-checking";
import L1Round from "./screens/interview/l1-round";
import OtherRecRound from "./screens/interview/rec-round";
import BrowserErrorBoundary from "./components/errors/BrowserErrorBoundary";
// import PositionPage1 from "./components/candidate-video-answers-summary/positionReport/positionPage1"
// import PositionPage2 from "./components/candidate-video-answers-summary/positionReport/positionPage2";
// import PositionPage3 from "./components/candidate-video-answers-summary/positionReport/positionPage3";
// import PositionPage4 from "./components/candidate-video-answers-summary/positionReport/positionPage4";
// import PositionPageAll from "./components/candidate-video-answers-summary";
import AuthCallback from "./components/auth-component/auth-callback";
import LandingPage from "./screens/public/LandingPageV2";
import PublicReferenceForm from "./screens/public-reference-form";
import CareersPage from "./screens/public/CareersPage";
import Apply from "./screens/public/Apply";
import CodingQuestion from "./screens/candidate/CodingQuestion";
import OfferAcceptance from "./screens/public/OfferAcceptance";
import AssessmentTake from "./screens/public/AssessmentTake";
import SelfSchedule from "./screens/public/SelfSchedule";
import CandidateAuthGuard from "./components/auth-component/candidate-auth-guard";
import CandidateLayout from "./layouts/CandidateLayout";
import CandidateDashboard from "./screens/candidate-portal/dashboard";
import MyInterviews from "./screens/candidate-portal/my-interviews";
import InterviewDetails from "./screens/candidate-portal/interview-details";
import InterviewFeedback from "./screens/candidate-portal/interview-feedback";
import PracticeInterview from "./screens/candidate-portal/practice";
import PrepResources from "./screens/candidate-portal/resources";
import CandidateProfile from "./screens/candidate-portal/profile";
import CandidateDocuments from "./screens/candidate-portal/documents";
import CandidateNotifications from "./screens/candidate-portal/notifications";
import CandidateHelp from "./screens/candidate-portal/help";
import PositionReport from "./components/interview-responses/positionReport";
import ReportPage1 from "./screens/candidate-report/report-page-1";
import OtpPage from "./screens/user-signup-signin/otp";
import AiInterview from "./screens/ai-interview/ai-interview";
import InviteAcceptance from "./screens/invite/invite-acceptance";
import CandidateAcceptancePage from "./screens/public/CandidateAcceptancePage";
import CandidateJoinPage from "./screens/public/CandidateJoinPage";
import { useEffect } from "react";
import { useGlobalContext } from "./context";
import { useDispatch } from "react-redux";
import { fetchBrandingCached } from "./redux/slices/entityBrandingSlice";

function App() {

  const dispatch = useDispatch();
  const { setRootColor, dynamicColorPage } = useGlobalContext();
  const hostname = window.location.hostname?.split(".")[0];


  useEffect(() => {
    const getBrandingApiCall = async () => {
      try {
        const response = await dispatch(fetchBrandingCached({ hostname }));
        const payload = response?.payload?.data || response?.payload || {};
        const { primaryColor, secondaryColor, logoURL } = payload.organizationBrandingDTO || {};
        const root = document.documentElement;
        root.style.setProperty('--primary-color', primaryColor || dynamicColorPage.primary);
        root.style.setProperty('--secondary-color', secondaryColor || dynamicColorPage.secondary);
        setRootColor({
          primary: primaryColor || dynamicColorPage.primary,
          secondary: secondaryColor || dynamicColorPage.secondary,
          logoUrl: logoURL || dynamicColorPage.logoUrl
        });
      } catch (err) {
        console.warn('getBranding failed; using defaults:', err?.message || err);
      }
    }
    getBrandingApiCall();
  }, []);

  return (
    <BrowserErrorBoundary>
      <Routes>
        <Route path="/ui-reference" element={<UiReference />} />
        <Route path="/signin/*" element={<SigninPage />} />
        <Route path="/otp/*" element={<OtpPage />} />
        <Route path="/interview/l1-round" element={<L1Round />} />
        {/* <Route path="/interview" element={<L1Round />} /> */}
        <Route path="/interview/recruiter-round" element={<OtherRecRound />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/entity-signup/*" element={<EntitySignupScreen />} />
        <Route path="/reset-password/*" element={<ResetPassword />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/confirm-email/*" element={<Confirm />} />
        <Route path="/device-check/*" element={<DeviceChecking />} />
        <Route path="/candidate-report" element={<ReportPage1 />} />
        <Route path="/position-report" element={<PositionReport />} />
        <Route
          path="/user/*"
          element={
            <>
              <AuthComponent>
                <User />
              </AuthComponent>
            </>
          }
        />
        <Route
          path="/admin/*"
          element={
            <AuthComponent>
              <Admin />
            </AuthComponent>
          }
        />
        <Route path="/ai-interview/:token" element={<AiInterview />} />
        <Route path="/invite/:token" element={<InviteAcceptance />} />
        <Route path="/interview/respond/:token" element={<CandidateAcceptancePage />} />
        <Route path="/interview/join/:inviteToken" element={<CandidateJoinPage />} />
        <Route path="/interview/*" element={<Interview />} />
        <Route path="/oauth2/callback" element={<AuthCallback />} />
        <Route path="/reference/:formToken" element={<PublicReferenceForm />} />
        <Route path="/careers/:slug" element={<CareersPage />} />
        <Route path="/apply/:jobId" element={<Apply />} />
        <Route path="/offer/:token" element={<OfferAcceptance />} />
        <Route path="/assessment/:token" element={<AssessmentTake />} />
        <Route path="/schedule/:token" element={<SelfSchedule />} />
        <Route path="/candidate/coding/:questionId" element={<CodingQuestion />} />
        <Route element={<CandidateAuthGuard />}>
          <Route element={<CandidateLayout />}>
            <Route path="/candidate/dashboard" element={<CandidateDashboard />} />
            <Route path="/candidate/interviews" element={<MyInterviews />} />
            <Route path="/candidate/interview/:jobId" element={<InterviewDetails />} />
            <Route path="/candidate/interviews/:interviewId" element={<InterviewFeedback />} />
            <Route path="/candidate/practice" element={<PracticeInterview />} />
            <Route path="/candidate/resources" element={<PrepResources />} />
            <Route path="/candidate/profile" element={<CandidateProfile />} />
            <Route path="/candidate/documents" element={<CandidateDocuments />} />
            <Route path="/candidate/notifications" element={<CandidateNotifications />} />
            <Route path="/candidate/help" element={<CandidateHelp />} />
          </Route>
        </Route>
        <Route path="/" element={<LandingPage />} />
        <Route path="/*" element={<NoRouteFound navigateToPath={"/"} />} />
      </Routes>
    </BrowserErrorBoundary>
  );
}

export default App;
